import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import LayoutComponent from './LayoutComponent';

const sectionContainsPath = (section, pathname) =>
  (section.items || []).some((item) => {
    if (item.href && item.href === pathname) return true;
    return (item.subItems || []).some((sub) => sub.href && sub.href === pathname);
  });

const buildExpandedFromRoute = (sections, pathname) => {
  const next = {};
  sections.forEach((section) => {
    const key = section.key;
    if (!key) return;
    if (!section.collapsible) {
      next[key] = true;
      return;
    }
    next[key] = sectionContainsPath(section, pathname);
  });
  return next;
};

/**
 * Estilo "quiet rail": flat, tipografía clara, sin tarjetas ni badges.
 * Jerarquía por peso/color/espaciado, no por cajas.
 */
const SidebarLeft = ({
  sections = [],
  activeItem = null,
  isDarkMode = false,
  width = 180,
  collapsed = false,
  onToggle,
  onResize,
  adminNav = null,
  footerNavItems = null,
  className = '',
  ...props
}) => {
  void activeItem;
  const [expandedSections, setExpandedSections] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [hoveredSectionKey, setHoveredSectionKey] = useState(null);
  const [footerHoveredKey, setFooterHoveredKey] = useState(null);
  const [showToggle, setShowToggle] = useState(false);
  const currentTheme = useTheme();
  const location = useLocation();
  const sectionsRef = useRef(sections);

  const sectionsSignature = useMemo(
    () => sections.map((s) => s.key).join('|'),
    [sections]
  );

  sectionsRef.current = sections;

  useLayoutEffect(() => {
    setExpandedSections(buildExpandedFromRoute(sectionsRef.current, location.pathname));
  }, [location.pathname, sectionsSignature]);

  const HEADER_OFFSET_PX = 64;
  const FOOTER_OFFSET_PX = 32;
  const SIDEBAR_HEIGHT = `calc(100vh - ${HEADER_OFFSET_PX + FOOTER_OFFSET_PX}px)`;

  const sidebarCustomStyles = {
    width: collapsed ? 0 : `${width}px`,
    height: SIDEBAR_HEIGHT,
    padding: collapsed ? 0 : '18px 10px 10px',
    overflowX: 'hidden',
    overflowY: collapsed ? 'visible' : 'hidden',
    borderRight: collapsed
      ? 'none'
      : `1px solid ${currentTheme.colors.border.primary}`,
    backgroundColor: currentTheme.colors.secondary,
    transition: 'width 0.22s ease, padding 0.22s ease, background-color 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    ...props.style,
  };

  const navStackStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: 8,
  };

  const adminFooterStyles = {
    flex: '0 0 auto',
    marginTop: 'auto',
    paddingTop: 10,
    paddingBottom: 4,
    borderTop: `1px solid ${currentTheme.colors.border.primary}`,
    backgroundColor: currentTheme.colors.secondary,
    zIndex: 1,
    display: 'grid',
    gap: 2,
  };

  const sectionStyles = (isLast) => ({
    paddingBottom: 6,
    marginBottom: isLast ? 0 : 6,
    borderBottom: isLast
      ? 'none'
      : `1px solid ${currentTheme.colors.border.primary}`,
  });

  const getSectionTitleStyles = ({ active, hovered, collapsible }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 8px',
    margin: 0,
    fontSize: 10.5,
    fontWeight: active ? 700 : 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: active
      ? currentTheme.colors.text.primary
      : hovered
        ? currentTheme.colors.text.primary
        : currentTheme.colors.text.muted,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 6,
    cursor: collapsible ? 'pointer' : 'default',
    userSelect: 'none',
    lineHeight: 1.2,
    transition: 'color 0.15s ease',
  });

  const chevronStyles = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: active
      ? currentTheme.colors.text.secondary
      : currentTheme.colors.text.muted,
    opacity: 0.75,
  });

  const itemsListStyles = {
    listStyle: 'none',
    padding: '0 0 4px',
    margin: 0,
    overflow: 'hidden',
    transition: 'max-height 0.22s ease, opacity 0.18s ease',
  };

  const getItemStyles = ({ active, hovered, isSub }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    width: '100%',
    boxSizing: 'border-box',
    padding: isSub ? '6px 10px 6px 12px' : '7px 10px',
    marginBottom: 1,
    borderRadius: 8,
    textDecoration: 'none',
    cursor: 'pointer',
    position: 'relative',
    fontSize: isSub ? 12.5 : 13,
    fontWeight: active ? 600 : 450,
    letterSpacing: active ? '0.01em' : 0,
    lineHeight: 1.25,
    color: active
      ? currentTheme.colors.interactive.primary
      : hovered
        ? currentTheme.colors.text.primary
        : currentTheme.colors.text.secondary,
    backgroundColor: active
      ? `${currentTheme.colors.interactive.primary}12`
      : hovered
        ? currentTheme.colors.hover
        : 'transparent',
    transition: 'background-color 0.12s ease, color 0.12s ease',
  });

  const iconWrapStyles = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    flexShrink: 0,
    color: active
      ? currentTheme.colors.interactive.primary
      : currentTheme.colors.text.muted,
    opacity: active ? 1 : 0.72,
  });

  const labelStyles = {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const subListStyles = {
    listStyle: 'none',
    padding: 0,
    margin: '1px 0 4px 14px',
    borderLeft: `1px solid ${currentTheme.colors.border.primary}`,
    paddingLeft: 4,
  };

  const toggleButtonStyles = {
    position: 'fixed',
    top: '50%',
    left: collapsed ? 6 : width - 14,
    transform: `translateY(-50%) ${showToggle ? 'scale(1)' : 'scale(0.92)'}`,
    width: 26,
    height: 26,
    borderRadius: '50%',
    border: `1px solid ${currentTheme.colors.border.primary}`,
    backgroundColor: currentTheme.colors.primary,
    color: currentTheme.colors.text.secondary,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    opacity: showToggle ? 1 : 0,
    pointerEvents: showToggle ? 'auto' : 'none',
    transition: 'opacity 0.15s ease, transform 0.15s ease, left 0.22s ease',
    zIndex: (currentTheme.zIndex?.fixed || 1030) + 20,
  };

  const collapsedRevealStyles = {
    position: 'fixed',
    top: HEADER_OFFSET_PX,
    left: 0,
    width: 12,
    height: SIDEBAR_HEIGHT,
    zIndex: (currentTheme.zIndex?.fixed || 1030) + 15,
  };

  const handleSectionToggle = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const isItemActive = (item) => {
    if (!item?.href) return false;
    return location.pathname === item.href;
  };

  const isExpanded = (section) => {
    if (!section.collapsible) return true;
    return expandedSections[section.key] === true;
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e) => {
      if (onResize) onResize(e.clientX);
    };
    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onResize]);

  const resizeHandleStyles = {
    position: 'fixed',
    top: HEADER_OFFSET_PX,
    left: Math.max(0, width - 4),
    width: 8,
    height: SIDEBAR_HEIGHT,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: (currentTheme.zIndex?.sticky || 1020) + 5,
  };

  const renderNavLink = (item, { isSub = false } = {}) => {
    const itemKey = item.key || item.href || item.text;
    const active = isItemActive(item);
    const hovered = hoveredKey === itemKey;

    return (
      <Link
        to={item.href || '#'}
        style={getItemStyles({ active, hovered, isSub })}
        onMouseEnter={() => setHoveredKey(itemKey)}
        onMouseLeave={() => setHoveredKey(null)}
        onClick={item.onClick}
      >
        {item.icon && (
          <span style={iconWrapStyles(active)}>{item.icon}</span>
        )}
        <span style={labelStyles}>{item.text}</span>
      </Link>
    );
  };

  return (
    <>
      <LayoutComponent
        componentType="sidebar-left"
        isDarkMode={isDarkMode}
        className={className}
        customStyles={sidebarCustomStyles}
        onMouseEnter={() => !collapsed && setShowToggle(true)}
        onMouseLeave={() => !collapsed && setShowToggle(false)}
      >
        {!collapsed && (
          <>
            <div style={navStackStyles}>
              {sections.map((section, sectionIndex) => {
                const sectionKey = section.key || sectionIndex;
                const expanded = isExpanded(section);
                const isCollapsible = Boolean(section.collapsible);
                const sectionActive = sectionContainsPath(section, location.pathname);
                const sectionHovered = hoveredSectionKey === sectionKey;
                const showItems = !isCollapsible || expanded;
                const isLast = sectionIndex === sections.length - 1;

                return (
                  <div key={sectionKey} style={sectionStyles(isLast && !(footerNavItems?.length || adminNav))}>
                    <div
                      style={getSectionTitleStyles({
                        active: sectionActive,
                        hovered: sectionHovered,
                        collapsible: isCollapsible,
                      })}
                      onClick={() => isCollapsible && handleSectionToggle(section.key)}
                      onMouseEnter={() => setHoveredSectionKey(sectionKey)}
                      onMouseLeave={() => setHoveredSectionKey(null)}
                      role={isCollapsible ? 'button' : undefined}
                      tabIndex={isCollapsible ? 0 : undefined}
                      aria-expanded={isCollapsible ? expanded : undefined}
                      onKeyDown={(e) => {
                        if (isCollapsible && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleSectionToggle(section.key);
                        }
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>{section.title}</span>
                      {isCollapsible && (
                        <span style={chevronStyles(sectionActive)}>
                          {expanded ? <ChevronDown size={14} strokeWidth={1.75} /> : <ChevronRight size={14} strokeWidth={1.75} />}
                        </span>
                      )}
                    </div>

                    <ul
                      style={{
                        ...itemsListStyles,
                        maxHeight: showItems ? 2000 : 0,
                        opacity: showItems ? 1 : 0,
                        pointerEvents: showItems ? 'auto' : 'none',
                      }}
                    >
                      {(section.items || []).map((item, itemIndex) => (
                        <li key={item.key || itemIndex}>
                          {renderNavLink(item)}
                          {item.subItems && item.subItems.length > 0 && (
                            <ul style={subListStyles}>
                              {item.subItems.map((subItem, subIndex) => (
                                <li key={subItem.key || subIndex}>
                                  {renderNavLink(subItem, { isSub: true })}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {(footerNavItems?.length || adminNav) ? (
              <div style={adminFooterStyles}>
                {(footerNavItems?.length ? footerNavItems : [adminNav]).map((item) => {
                  const itemKey = item.key || item.href || item.text;
                  const active = Boolean(item.active);
                  return (
                    <Link
                      key={itemKey}
                      to={item.href || '#'}
                      style={{
                        ...getItemStyles({
                          active,
                          hovered: footerHoveredKey === itemKey,
                          isSub: false,
                        }),
                        fontWeight: active ? 750 : 650,
                        letterSpacing: '0.02em',
                      }}
                      onMouseEnter={() => setFooterHoveredKey(itemKey)}
                      onMouseLeave={() => setFooterHoveredKey(null)}
                    >
                      <span style={iconWrapStyles(active)}>
                        {item.icon || <Settings size={16} />}
                      </span>
                      <span style={labelStyles}>{item.text}</span>
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </LayoutComponent>

      {!collapsed && onResize && (
        <div
          style={resizeHandleStyles}
          onMouseDown={handleMouseDown}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = `${currentTheme.colors.interactive.primary}28`;
          }}
          onMouseLeave={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Ajustar ancho"
        />
      )}

      {onToggle && collapsed && (
        <div
          style={collapsedRevealStyles}
          onMouseEnter={() => setShowToggle(true)}
          onMouseLeave={() => setShowToggle(false)}
        />
      )}

      {onToggle && (
        <button
          type="button"
          style={toggleButtonStyles}
          onClick={onToggle}
          onMouseEnter={() => setShowToggle(true)}
          onMouseLeave={() => setShowToggle(false)}
          aria-label={collapsed ? 'Mostrar sidebar' : 'Ocultar sidebar'}
          title={collapsed ? 'Mostrar sidebar' : 'Ocultar sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      )}
    </>
  );
};

export default SidebarLeft;
